import _ from "lodash";
import React, { useState, useMemo, useCallback } from "react";
import { Button, Modal } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";

import client from "lib/apolloClient/client";
import Loading from "UIComponents/Loading";

import DualPanelManageMembers from "AppComponents/DualPanelManageMembers";
import DualPanelManageMembersV2 from "AppComponents/DualPanelManageMembersV2";
import { DUAL_PANEL_VIEW_MODE } from "AppComponents/DualPanelManageMembersV2/module/utils";
import { getUserGroupChannelCountQuery } from "MessagingV2/modules/MessagingQuery";
import UsersAndCohortsQueryWrapper, {
  metadataProps,
} from "MessagingV2/components/Channels/AddMembersV3/UsersAndCohortsQueryWrapper";
import { getOrganizationId, getUserId } from "modules/CommonSelectors";
import {
  CHAT_TYPE_ENUM,
  getUserTypeOption,
  isMessagingCurriculumWiseSplitEnabled,
  isDualPanelRevampEnabled,
} from "MessagingV2/modules/Utils";
import { getDualPanelQueryWrapperConfig } from "MessagingV2/components/DualPanelQueryWrapper";
import Filter, {
  FILTER_KEYS,
} from "MessagingV2/components/Channels/CreateChannelModal/Filter";
import classes from "./AddMembersModal.scss";

const styles = {
  modalBodyStyle: {
    padding: "0px",
    overflow: "hidden",
  },
  modalHeaderStyle: {
    padding: "16px 24px",
    marginBottom: 0,
  },
};

const overrideClasses = {
  usersViewContainer: classes.usersViewContainer,
};

const SHORTCUT_CODE = {
  ENTER: { code: "Enter", shiftKey: true },
};

const AddMembersModal = props => {
  const {
    onClose,
    onBackClick,
    onClickNext,
    handleAddUsers,
    handleRemoveUsers,
    title,
    chatId,
    selectedUsers = [],
    previousUsers = [],
    onCreateChatToChannel,
  } = props;

  const initUsersState = useMemo(() => {
    const previousUsersIdsSet = new Set(previousUsers.map(user => user.id));
    return _.reduce(
      selectedUsers,
      (acc, user) => {
        if (!previousUsersIdsSet.has(user.id)) {
          acc[user.id] = user;
        }
        return acc;
      },
      {}
    );
  }, []);

  const { t } = useI18n(["common"]);

  const [showLoader, setShowLoader] = useState(false);
  const [typeFilter, setTypeFilter] = useState(DUAL_PANEL_VIEW_MODE.MEMBERS);
  const [selectedFilters, setSelectedFilters] = useState({
    [FILTER_KEYS.SCHOOL]: [],
    [FILTER_KEYS.USER_TYPE]: [],
  });

  const customQueryWrapperFields = useMemo(
    () => ({ typeFilter, selectedFilters }),
    [typeFilter, selectedFilters]
  );

  const { metadata, renderQueryWrapper } = useMemo(
    getDualPanelQueryWrapperConfig,
    []
  );

  const renderUsersAndCohortsQueryWrapper = useCallback(
    queryProps => (
      <UsersAndCohortsQueryWrapper
        includePaidUsersOnly={isMessagingCurriculumWiseSplitEnabled()}
        {...queryProps}
      />
    ),
    []
  );

  const previousUsersIdSet = useMemo(
    () => new Set(previousUsers.map(u => u.id)),
    [previousUsers]
  );
  const newUserAdded = _.some(
    selectedUsers,
    user => !previousUsersIdSet.has(user.id)
  );

  const handleClickNext = async () => {
    try {
      setShowLoader(true);
      const userIds = _.map(selectedUsers, "id");
      const exactUserIds = _.uniq([...userIds, getUserId()]);

      const response = await client.query({
        query: getUserGroupChannelCountQuery,
        variables: {
          organizationId: getOrganizationId(),
          input: {
            type: [CHAT_TYPE_ENUM.CHANNEL_CUSTOM],
            search: {
              exactUserIds,
            },
          },
          messageInput: {
            inChatIds: [chatId],
            first: 1,
          },
        },

        fetchPolicy: "network-only",
      });

      const existingChannelCount = _.get(
        response,
        "data.node.chats.totalCount",
        0
      );

      const emptyMessages = _.isEmpty(
        _.get(response, "data.node.messages.edges", [])
      );

      if (_.eq(existingChannelCount, 0) && emptyMessages) {
        await onCreateChatToChannel();
      } else {
        onClickNext(response);
      }
    } catch {
      // do nothing
    } finally {
      setShowLoader(false);
    }
  };

  const renderBody = () => {
    if (showLoader) return <Loading />;

    if (isDualPanelRevampEnabled()) {
      return (
        <DualPanelManageMembersV2
          prevUsers={previousUsers}
          initUsersState={initUsersState}
          handleAddUsers={handleAddUsers}
          handleRemoveUsers={handleRemoveUsers}
          metadataProps={metadata}
          renderQueryWrapper={renderQueryWrapper}
          overrideClasses={overrideClasses}
          filterComponent={
            <Filter
              typeFilter={typeFilter}
              setTypeFilter={setTypeFilter}
              selectedFilters={selectedFilters}
              onFiltersChange={setSelectedFilters}
            />
          }
          customQueryWrapperFields={customQueryWrapperFields}
        />
      );
    }

    return (
      <DualPanelManageMembers
        prevUsers={previousUsers}
        initUsersState={initUsersState}
        handleAddUsers={handleAddUsers}
        handleRemoveUsers={handleRemoveUsers}
        metadataProps={metadataProps}
        dsVersion="2.0"
        searchPlaceholder="common:search_member_placeholder"
        showTypeFilter
        typeFilterOptions={getUserTypeOption({ t })}
        renderQueryWrapper={renderUsersAndCohortsQueryWrapper}
        overrideClasses={overrideClasses}
      />
    );
  };

  return (
    <Modal
      height="680px"
      width="1000px"
      onClose={onClose}
      isOpen
      dsVersion="2.0"
      footerSeparator
    >
      <Modal.Header
        style={styles.modalHeaderStyle}
        title={title}
        isClosable
        hasSeparator
      />

      <Modal.Body style={styles.modalBodyStyle}>{renderBody()}</Modal.Body>
      <Modal.Footer hasSeparator>
        <div className={classes.footerContainer}>
          <Button
            variant="neutral"
            type="outlined"
            onClick={onBackClick}
            dsVersion="2.0"
            disabled={showLoader}
          >
            {t("common:back")}
          </Button>
          <Button
            dsVersion="2.0"
            variant="progressive"
            type="fill"
            onClick={handleClickNext}
            disabled={!newUserAdded || showLoader}
            shortcut={SHORTCUT_CODE.ENTER}
          >
            {t("common:next")}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default AddMembersModal;
